import { currencyFormat } from 'utils/currencyManipulation';
import { IOfferingInfo } from 'utils/globalTypes';
import { roundToOne, roundToTwo, toPercentageFixedFormat, toPremiumFormat } from 'utils/math';
import { isNumeric } from 'utils/object';
import { calculatePremium } from 'utils/orderUtils';

import { OfferingStatus, OfferingType } from './networkInterfaces';

interface IInvestmentData {
  id?: string;
  name?: string;
  offering_id?: string;
  offering_name?: string;
  offering_status?: number;
  offering_type?: number;
  price?: number;
  buy_price?: number;
  start_at?: number;
  end_at?: number;
  quantity?: number;
  quantity_pending_int_buy?: number;
  quantity_pending_int_buy_investor?: number;
  quantity_pending_bid_buy_investor?: number;
  quantity_pending_bid_sell_investor?: number;
  quantity_executed_int_buy_investor?: number;
  quantity_executed_bid_buy_investor?: number;
  avg_price_executed_int_buy_investor?: number;
  avg_price_executed_bid_buy_investor?: number;
  total_shares_sell_investor?: number;
  total_shares_buy_investor?: number;
  total_dividends_investor?: number;

  [key: string]: unknown;
}

export const DAY_IN_MILLIS = 1000 * 60 * 60 * 24;
export const EMPTY_VALUE = '—';

export const calcInvestmentDetails = (investment: IInvestmentData) => {
  const funding_progress =
    investment.offering_status === OfferingStatus.active &&
    investment.offering_type == OfferingType.primary &&
    investment.quantity_pending_int_buy &&
    investment.quantity
      ? roundToTwo((investment.quantity_pending_int_buy / investment.quantity) * 100)
      : undefined;

  const current_time = new Date().getTime();
  const days_left =
    investment.offering_status === OfferingStatus.active &&
    investment.offering_type == OfferingType.primary &&
    investment.end_at &&
    current_time < investment.end_at
      ? Math.trunc((investment.end_at - current_time) / DAY_IN_MILLIS)
      : undefined;

  const days_start =
    investment.offering_status === OfferingStatus.active &&
    investment.offering_type == OfferingType.primary &&
    investment.start_at &&
    current_time < investment.start_at
      ? Math.trunc((investment.start_at - current_time) / DAY_IN_MILLIS)
      : undefined;

  const quantity_pending_investor_any =
    investment.quantity_pending_int_buy_investor ||
    investment.quantity_pending_bid_buy_investor ||
    investment.quantity_pending_bid_sell_investor;

  const capital_gain = investment.capital_gain
    ? (investment.capital_gain as number)
    : investment.total_shares_sell_investor &&
      investment.total_shares_buy_investor &&
      investment.total_shares_sell_investor - investment.total_shares_buy_investor;

  const total_return_investor =
    capital_gain &&
    investment.total_dividends_investor &&
    capital_gain + investment.total_dividends_investor;

  const total_return_pct_investor =
    total_return_investor &&
    investment.total_shares_buy_investor &&
    total_return_investor / investment.total_shares_buy_investor;

  return {
    ...investment,

    days_start,
    days_left,
    funding_progress,
    quantity_pending_investor_any,
    capital_gain,
    total_return_investor,
    total_return_pct_investor
  };
};

export const getPremiumSharePrice = (premium: number, price?: number) => {
  return price ? price * (1 + (premium ? premium / 100 : 0)) : undefined;
};

export const getPremiumImpliedCap = (
  premium: number,
  t12_noi?: number,
  implied_valuation?: number,
  equity?: number
) => {
  return t12_noi && implied_valuation && equity
    ? t12_noi / (implied_valuation - equity + equity * (1 + premium / 100))
    : undefined;
};

export const getPremiumCashYield = (premium: number, t12_yield?: number) => {
  return t12_yield ? t12_yield / (1 + premium / 100) : undefined;
};

const getValue = (
  value: number | undefined,
  formatValue: (value: number) => string,
  defVal = EMPTY_VALUE
) => (!!value ? formatValue(value) : defVal);

export const formatSharePrice = (
  price?: number | string | unknown,
  currency: string | unknown = 'USD',
  digits = 2
) => {
  return currencyFormat(price, 'en-US', currency, false, digits, digits);
};

export const formatSharePriceWithPremium = (
  price?: number,
  offeringPrice?: number,
  currency: string | unknown = 'USD',
  digits = 2,
  defVal = EMPTY_VALUE
) => {
  if (!price || !isNumeric(price)) return defVal;

  const premium = offeringPrice
    ? ` (${toPremiumFormat({
        value: calculatePremium(offeringPrice, price),
        callback: roundToOne
      })})`
    : '';
  return `${formatSharePrice(price, currency, digits)}${premium}`;
};

export const formatSharePremiumWithPrice = (
  price?: number,
  offeringPrice?: number,
  currency: string | unknown = 'USD',
  digits = 2,
  defVal = EMPTY_VALUE
) => {
  if (!price || !isNumeric(price)) return defVal;

  const priceFormat = formatSharePrice(price, currency, digits);
  const premiumFormat = toPremiumFormat({
    value: calculatePremium(offeringPrice, price),
    callback: roundToOne
  });

  return offeringPrice ? `${premiumFormat} (${priceFormat})` : priceFormat;
};

export const formatSharePremium = (
  price?: number,
  offeringPrice?: number,
  defVal = EMPTY_VALUE
) => {
  if (!price || !isNumeric(price)) return defVal;
  if (!offeringPrice || !isNumeric(offeringPrice)) return defVal;

  return toPremiumFormat({ value: calculatePremium(offeringPrice, price), callback: roundToOne });
};

export const formatQuantity = (price?: number | string | unknown, defVal = EMPTY_VALUE) => {
  if (!price || !isNumeric(price)) return defVal;
  return `${price}`;
};

export const formatQuantityWithHoldings = (
  quantity?: number | string | unknown,
  offeringQuantity?: number | string | unknown,
  defVal = EMPTY_VALUE
) => {
  if (!quantity || !isNumeric(quantity)) return defVal;

  const holdings = offeringQuantity
    ? ` (${toPercentageFixedFormat({ value: Number(quantity) / Number(offeringQuantity) })})`
    : '';
  return `${quantity}${holdings}`;
};

export const formatHoldingsWithQuantity = (
  quantity?: number | string | unknown,
  offeringQuantity?: number | string | unknown,
  defVal = EMPTY_VALUE
) => {
  if (!quantity || !isNumeric(quantity)) return defVal;

  const quantityFormat = formatQuantity(quantity);
  const holdingsFormat = toPercentageFixedFormat({
    value: Number(quantity) / Number(offeringQuantity)
  });

  return offeringQuantity ? `${holdingsFormat} (${quantityFormat})` : quantityFormat;
};

export const formatHoldingsInIssuer = (
  quantity?: number | string | unknown,
  offeringQuantity?: number | string | unknown,
  defVal = EMPTY_VALUE
) => {
  if (!quantity || !isNumeric(quantity)) return defVal;
  if (!offeringQuantity || !isNumeric(offeringQuantity)) return defVal;

  return toPercentageFixedFormat({ value: Number(quantity) / Number(offeringQuantity) });
};

export const formatAmount = (
  value?: number | string | unknown,
  currency: string | unknown = 'USD',
  digits = 2,
  defVal = EMPTY_VALUE
) => {
  if (!value || !isNumeric(value)) return defVal;

  return currencyFormat(value, 'en-US', currency, false, digits);
};

export const formatValuation = (
  value?: number | string | unknown,
  currency: string | unknown = 'USD',
  digits = 1,
  defVal = EMPTY_VALUE
) => {
  if (!value || !isNumeric(value)) return defVal;

  return currencyFormat(value, 'en-US', currency, true, digits);
};

export const formatOfferingSharePrice = (premium: number, offeringInfo: IOfferingInfo) => {
  return getValue(getPremiumSharePrice(premium, offeringInfo?.price), (val) =>
    formatSharePrice(val, offeringInfo?.currency)
  );
};

export const formatOfferingImpliedCap = (premium: number, offeringInfo: IOfferingInfo) => {
  return getValue(
    getPremiumImpliedCap(
      premium,
      offeringInfo?.t12_noi,
      offeringInfo?.implied_valuation,
      offeringInfo.equity
    ),
    (val) => toPercentageFixedFormat({ value: val })
  );
};

export const formatOfferingCashYield = (premium: number, offeringInfo: IOfferingInfo) => {
  return getValue(getPremiumCashYield(premium, offeringInfo?.t12_yield), (val) =>
    toPercentageFixedFormat({ value: val })
  );
};

export const isValueEmpty = (value?: string | number) => {
  return value === undefined || value === null || value === '';
};

export const formatEmpty = (value?: string | number, defVal = EMPTY_VALUE) => {
  return !isValueEmpty(value) ? value : defVal;
};

export const getTotal = (equity?: number, price?: number) => {
  return roundToTwo((getQuantity(equity) || 0) * (price || 0));
};

export const getQuantity = (equity?: number) => {
  return equity ? Math.floor(Number(equity) * 1000) : 0;
};

export const getEquity = (quantity?: number) => {
  return quantity ? quantity / 1000.0 : 0;
};

export const getQuantityByPrice = (total?: number, price?: number) => {
  if (!total || !price) return 0;
  return Math.floor(total / price);
};
